import { flatMap, size } from "lodash";
import { useInjection } from "@linkfluence/core/di";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  QueryRepository,
  QueryRepositoryUID,
} from "@radarly/core/queries/repositories/QueryRepository";
import { SearchRequestOptions } from "@radarly/models/queries";

const QUERIES_PER_PAGE = 20;

export function useSearchQueries(
  search: string,
  {
    limit = QUERIES_PER_PAGE,
    ...options
  }: Partial<SearchRequestOptions & { limit: number }>
) {
  const repository = useInjection<QueryRepository>(QueryRepositoryUID);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery(
      ["/queries/search", search, options],
      ({ pageParam = 0 }) => {
        return repository.searchQueries(
          search,
          { start: pageParam, limit },
          options
        );
      },
      {
        getNextPageParam: (lastQuery, allQueries) => {
          const isLastPage = size(lastQuery.queries) < limit;
          // returns new `start` value, not new page
          return isLastPage ? undefined : size(allQueries) * limit + 1;
        },
      }
    );
  const hasData = data && size(data.pages) > 0;
  return {
    loading: isLoading,
    queries: hasData ? flatMap(data.pages, (d) => d.queries) : [],
    hits: hasData ? data.pages[0].total : 0,
    fetchMore: fetchNextPage,
    canFetchMore: hasNextPage,
    isFetchingNextPage,
  };
}
